// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\supervisors\SupervisorControlReportPage.js

import React, { useState, useEffect } from 'react';
import { 
  Card, Form, Select, DatePicker, Input, Button, Row, Col, 
  Typography, message, Space, Divider, Alert, Tag, InputNumber, 
  Switch, Radio, Checkbox
} from 'antd';
import { 
  SafetyCertificateOutlined, UserOutlined, MedicineBoxOutlined, 
  SendOutlined, PlusOutlined, DeleteOutlined 
} from '@ant-design/icons';
import { useHistory, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axiosInstance from '../../services/axiosServicesVisit';
import dayjs from 'dayjs';

const { Text } = Typography;
const { Option } = Select;

// Options des potentiels demandées (A+, A, B+, B, C)
const POTENTIAL_OPTIONS = [
  { label: 'A+', value: 'A+' },
  { label: 'A', value: 'A' },
  { label: 'B+', value: 'B+' },
  { label: 'B', value: 'B' },
  { label: 'C', value: 'C' },
];

const SupervisorControlReportPage = () => {
  const history = useHistory();
  const [form] = Form.useForm();
  const user = useSelector((state) => state.signin.user);

  const [loading, setLoading] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);

  const [delegates, setDelegates] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [products, setProducts] = useState([]);
  const [samples, setSamples] = useState([]);

  const [allTeamMode, setAllTeamMode] = useState(false);
  const [unassignedMode, setUnassignedMode] = useState(false);

  // Surveillance des délégués sélectionnés
  const selectedDelegateIds = Form.useWatch('delegateIds', form);

  // ⚡ SURVEILLANCE DU TYPE D'INTERVENTION (VISITE DOUBLE, SUPERVISION, CREATION) ⚡
  const visitType = Form.useWatch('visitType', form);

  // 1. Chargement initial des délégués, produits et échantillons
  useEffect(() => {
    const token = localStorage.getItem('token') || 
                  localStorage.getItem('accessToken') || 
                  localStorage.getItem('token_auth') ||
                  user?.token;

    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    axiosInstance.get('/v1/supervisors/my-delegates', { headers: authHeaders })
      .then(res => setDelegates(res.data || []))
      .catch(() => message.error("Erreur chargement de vos délégués"));
  }, [user]);

  // ⚡ 1.bis Chargement dynamique des produits et échantillons (Superviseur + Délégués sélectionnés)
  useEffect(() => {
    const token = localStorage.getItem('token') || 
                  localStorage.getItem('accessToken') || 
                  localStorage.getItem('token_auth') ||
                  user?.token;

    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    const params = {};
    if (Array.isArray(selectedDelegateIds) && selectedDelegateIds.length > 0) {
      params.delegateIds = selectedDelegateIds.join(',');
    }

    axiosInstance.get('/v1/supervisors/available-products-and-samples', { params, headers: authHeaders })
      .then(res => {
        setProducts(res.data?.products || []);
        setSamples(res.data?.samples || []);
      })
      .catch(() => {
        console.error("Erreur chargement des produits et échantillons");
      });
  }, [selectedDelegateIds, user]);

  // 2. Rechargement des médecins selon délégués / toute équipe / non assignés
  useEffect(() => {
    const hasSelection = Array.isArray(selectedDelegateIds) && selectedDelegateIds.length > 0;
    
    if (!hasSelection && !allTeamMode && !unassignedMode) {
      setDoctors([]);
      return;
    }

    const token = localStorage.getItem('token') || 
                  localStorage.getItem('accessToken') || 
                  user?.token;
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    setLoadingDoctors(true);
    const params = {
      delegateIds: hasSelection ? selectedDelegateIds.join(',') : undefined,
      allTeam: allTeamMode,
      unassigned: unassignedMode
    };

    axiosInstance.get('/v1/supervisors/team-doctors', { params, headers: authHeaders })
      .then(res => setDoctors(res.data || []))
      .catch(() => message.error("Erreur chargement des médecins"))
      .finally(() => setLoadingDoctors(false));
  }, [selectedDelegateIds, allTeamMode, unassignedMode, user]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('accessToken') || 
                    user?.token;
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      const payload = {
        delegateIds: values.delegateIds || [],
        delegateId: values.delegateIds?.[0],
        personId: values.personId,
        date: values.date ? values.date.toISOString() : new Date().toISOString(),
        visitType: values.visitType,
        rating: values.rating,
        // ⚡ ENVOI DES DEUX POTENTIELS ⚡
        doctorPotential: values.doctorPotential,
        productPotential: values.productPotential,
        comment: values.comment,
        productIds: values.productIds || [],
        samples: (values.samples || []).filter(s => s && s.sampleId && s.quantity > 0)
      };

      await axiosInstance.post('/v1/supervisors/control-report', payload, { headers: authHeaders });
      message.success("Rapport de contrôle enregistré avec succès !");
      form.resetFields();
      history.push('/rapports-superviseurs');
    } catch (err) {
      message.error(err.response?.data?.message || "Erreur lors de l'enregistrement.");
    } finally {
      setLoading(false);
    }
  };

  const userRole = user?.role?.name?.toLowerCase();
  const isSupervisor = userRole === "superviseur";
  const isAdmin = userRole === "admin" || userRole === "administrateur" || user?.role?.id === 1;

  if (!isSupervisor && !isAdmin) {
    return <Redirect to="/unauthorized" />;
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      <Card 
        bordered={false}
        style={{ borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}
        title={
          <Space>
            <SafetyCertificateOutlined style={{ color: '#0e519b', fontSize: '22px' }} />
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
              Rapport de Visite de Contrôle / Accompagnement
            </span>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          message="Espace réservé à la supervision terrain"
          description="1. Sélectionnez le ou les délégués que vous accompagnez sur le terrain.
                      2. Choisissez le médecin visité parmi leurs portefeuilles combinés ou les médecins non assignés."
          style={{ marginBottom: 24, borderRadius: 8 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ 
            date: dayjs(),
            visitType: 'ACCOMPAGNEMENT',
            rating: 4
          }}
        >
          {/* ÉTAPE 1 : DÉLÉGUÉS ACCOMPAGNÉS (MULTISELECT) ET DATE */}
          <div style={{ background: '#f0f5ff', padding: '16px 20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #adc6ff' }}>
            <Text strong style={{ color: '#002766', fontSize: '14px', display: 'block', marginBottom: 12 }}>
              ÉTAPE 1 : Délégué(s) accompagné(s) & Date de visite
            </Text>

            <Row gutter={20}>
              <Col xs={24} md={14}>
                <Form.Item
                  name="delegateIds"
                  label={<Text strong><UserOutlined /> Délégué(s) supervisé(s) / accompagné(s)</Text>}
                  rules={[{ required: true, message: 'Veuillez choisir au moins un délégué accompagné' }]}
                >
                  <Select 
                    mode="multiple"
                    allowClear
                    placeholder="Choisir un ou plusieurs délégués..." 
                    showSearch 
                    optionFilterProp="label"
                    maxTagCount="responsive"
                    onChange={() => form.setFieldsValue({ personId: undefined })}
                    options={delegates.map(d => ({
                      value: d.id,
                      label: `${d.firstname} ${d.lastname} ${d.category ? `(${d.category})` : ''}`
                    }))}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={10}>
                <Form.Item
                  name="date"
                  label={<Text strong>Date et Heure du contrôle</Text>}
                  rules={[{ required: true, message: 'Veuillez renseigner la date' }]}
                >
                  <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* ÉTAPE 2 : MÉDECIN VISITÉ */}
          <div style={{ background: '#fafafa', padding: '16px 20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e8e8e8' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
              <Text strong style={{ color: '#1f1f1f', fontSize: '14px' }}>
                ÉTAPE 2 : Sélection du Médecin visité
              </Text>

              <Space align="center" size={16} wrap>
                <Checkbox 
                  checked={unassignedMode} 
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setUnassignedMode(checked);
                    if (checked) {
                      setAllTeamMode(false);
                    }
                    form.setFieldsValue({ personId: undefined });
                  }} 
                >
                  <Text strong style={{ color: '#d46b08', fontSize: '12px' }}>
                    ⚠️ Médecins non assignés (Créations)
                  </Text>
                </Checkbox>

                <Space align="center">
                  <Text type="secondary" style={{ fontSize: '12px' }}>Élargir à toute mon équipe :</Text>
                  <Switch 
                    checked={allTeamMode} 
                    disabled={unassignedMode}
                    onChange={(checked) => {
                      setAllTeamMode(checked);
                      form.setFieldsValue({ personId: undefined });
                    }} 
                  />
                </Space>
              </Space>
            </div>

            <Row gutter={20}>
              <Col xs={24}>
                <Form.Item
                  name="personId"
                  label={
                    <Space>
                      <MedicineBoxOutlined style={{ color: '#1890ff' }} />
                      <Text strong>Médecin visité</Text>
                      {unassignedMode ? (
                        <Tag color="orange" style={{ fontWeight: 'bold' }}>
                          ⚠️ Médecins non assignés ({doctors.length} médecins disponibles)
                        </Tag>
                      ) : (selectedDelegateIds?.length > 0 || allTeamMode) && (
                        <Tag color={allTeamMode ? "purple" : "blue"}>
                          {allTeamMode ? "Toute l'équipe" : `${selectedDelegateIds?.length} délégué(s) sélectionné(s)`} ({doctors.length} médecins disponibles)
                        </Tag>
                      )}
                    </Space>
                  }
                  rules={[{ required: true, message: 'Veuillez sélectionner le médecin' }]}
                >
                  <Select
                    showSearch
                    placeholder={
                      unassignedMode 
                        ? "Sélectionnez un médecin non assigné..." 
                        : (!selectedDelegateIds || selectedDelegateIds.length === 0) && !allTeamMode 
                          ? "Sélectionnez d'abord un ou plusieurs délégués à l'étape 1" 
                          : "Tapez un nom de médecin, une spécialité ou une région..."
                    }
                    disabled={(!selectedDelegateIds || selectedDelegateIds.length === 0) && !allTeamMode && !unassignedMode}
                    loading={loadingDoctors}
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={doctors.map(doc => {
                      const dels = doc.assignedDelegates?.length > 0 
                        ? doc.assignedDelegates.map(d => d.name).join(', ') 
                        : '⚠️ Non assigné / Création';

                      return {
                        value: doc.id,
                        label: `${doc.isPartner ? '⭐ ' : ''}${doc.name} (${doc.specialty} - ${doc.region}) - [${dels}]`
                      };
                    })}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Divider style={{ margin: '16px 0' }} />

          {/* ÉTAPE 3 : ÉVALUATION DU COACHING */}
          <div style={{ background: '#fffbe6', padding: '16px 20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #ffe58f' }}>
            <Text strong style={{ color: '#874d00', fontSize: '14px', display: 'block', marginBottom: 12 }}>
              ÉTAPE 3 : Évaluation
            </Text>

            <Row gutter={20}>
              <Col xs={24}>
                <Form.Item name="visitType" label={<Text strong>Type d'intervention</Text>}>
                  <Radio.Group buttonStyle="solid">
                    <Radio.Button value="VISITE DOUBLE">Visite en double</Radio.Button>
                    <Radio.Button value="SUPERVISION">Supervision</Radio.Button>
                    {/* ⚡ NOUVEAU BOUTON CRÉATION ⚡ */}
                    <Radio.Button value="CREATION" style={{ fontWeight: 'bold' }}>Création</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            {/* ⚡ DEUX SELECTS AFFICHÉS UNIQUEMENT SI CRÉATION EST SÉLECTIONNÉ ⚡ */}
            {visitType === 'CREATION' && (
              <div style={{ 
                background: '#ffffff', 
                padding: '16px', 
                borderRadius: '8px', 
                border: '1px dashed #d46b08', 
                marginBottom: '16px' 
              }}>
                <Text strong style={{ color: '#d46b08', display: 'block', marginBottom: 12 }}>
                  🎯 Qualification de la Création :
                </Text>

                <Row gutter={20}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="doctorPotential"
                      label={<Text strong>Potentiel Médecin</Text>}
                      rules={[{ required: true, message: 'Veuillez choisir le potentiel médecin' }]}
                    >
                      <Select 
                        placeholder="Choisir le potentiel médecin (A+, A, B+, B, C)..." 
                        options={POTENTIAL_OPTIONS} 
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      name="productPotential"
                      label={<Text strong>Potentiel Produit</Text>}
                      rules={[{ required: true, message: 'Veuillez choisir le potentiel produit' }]}
                    >
                      <Select 
                        placeholder="Choisir le potentiel produit (A+, A, B+, B, C)..." 
                        options={POTENTIAL_OPTIONS} 
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            )}

            <Form.Item
              name="comment"
              label={<Text strong>Observations du Superviseur</Text>}
              rules={[{ required: true, message: 'Veuillez saisir vos observations' }]}
            >
              <Input.TextArea 
                rows={4} 
                placeholder="Ex : Maîtrise des arguments scientifiques, ponctualité, relationnel avec le médecin, points à retravailler..." 
              />
            </Form.Item>
          </div>

          <Divider style={{ margin: '16px 0' }} />

          {/* 4. Produits présentés */}
          <Form.Item 
    name="productIds" 
    label={
      <Space>
        <Text strong>Produits présentés</Text>
        <Tag color="blue">{products.length} disponible(s)</Tag>
      </Space>
    }
  >
    <Select 
      mode="multiple" 
      placeholder={products.length > 0 ? "Sélectionnez les produits présentés" : "Aucun produit assigné"} 
      allowClear
      optionFilterProp="children"
    >
      {products.map(p => (
        <Option key={p.id} value={p.id}>{p.name}</Option>
      ))}
    </Select>
  </Form.Item>

  {/* 5. Échantillons distribués */}
  <Space style={{ marginBottom: 8 }}>
    <Text strong>Échantillons :</Text>
    <Tag color="green">{samples.length} disponible(s)</Tag>
  </Space>
  <Form.List name="samples">
    {(fields, { add, remove }) => (
      <>
        {fields.map(({ key, name, ...restField }) => (
          <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
            <Form.Item
              {...restField}
              name={[name, 'sampleId']}
              rules={[{ required: true, message: 'Choisir échantillon' }]}
              style={{ width: 300, margin: 0 }}
            >
              <Select 
                showSearch
                placeholder={samples.length > 0 ? "Choisir l'échantillon" : "Aucun échantillon assigné"}
                optionFilterProp="children"
              >
                {samples.map(s => (
                  <Option key={s.id} value={s.id}>{s.name}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              {...restField}
              name={[name, 'quantity']}
              rules={[{ required: true, message: 'Qté requise' }]}
              style={{ width: 120, margin: 0 }}
            >
              <InputNumber min={1} placeholder="Qté" style={{ width: '100%' }} />
            </Form.Item>

            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
          </Space>
        ))}

        <Form.Item>
          <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ maxWidth: 300 }}>
            Ajouter un échantillon
          </Button>
        </Form.Item>
      </>
    )}
  </Form.List>

          <Divider style={{ margin: '20px 0' }} />

          {/* Boutons d'action */}
          <Form.Item style={{ textAlign: 'right', marginTop: 20 }}>
            <Space>
              <Button onClick={() => history.push('/rapports-superviseurs')}>Annuler</Button>
              <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={loading} size="large">
                Enregistrer la Visite de Contrôle
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default SupervisorControlReportPage;